import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchBerichteApi,
  fetchBerichtHtmlApi,
  generiereBerichtApi,
  fetchStatusApi,
  fetchStrategieApi,
  freezeStrategieApi,
} from './berichteApi';

/**
 * Redux-Slice fuer den Bereich "Berichte": Liste + HTML des gewaehlten
 * Berichts, Laufstatus/Fortschritt der Hintergrund-Jobs sowie die hinterlegte
 * factorlab-Strategie.
 */
export const fetchBerichte = createAsyncThunk('berichte/fetchListe', async () => {
  return (await fetchBerichteApi()) ?? [];
});

export const fetchBerichtHtml = createAsyncThunk('berichte/fetchHtml', async (id) => {
  return { id, html: await fetchBerichtHtmlApi(id) };
});

// typ = 'eigen' | 'factorlab' | undefined (= beide).
export const generiereBericht = createAsyncThunk('berichte/generieren', async (typ) => {
  return generiereBerichtApi(typ);
});

export const fetchStatus = createAsyncThunk('berichte/status', async () => {
  return (await fetchStatusApi()) ?? [];
});

export const fetchStrategie = createAsyncThunk('berichte/strategie', async () => {
  return (await fetchStrategieApi()) ?? null;
});

export const freezeStrategie = createAsyncThunk('berichte/freeze', async () => {
  return freezeStrategieApi();
});

const initialState = {
  liste: [],
  status: 'idle', // idle | loading | succeeded | failed
  error: null,
  htmlCache: {}, // id -> HTML-String
  gewaehlteId: null,
  htmlStatus: 'idle',
  generiereStatus: 'idle',
  jobs: [], // Laufstatus je Job-Art (eigen | factorlab | freeze)
  strategie: null, // Metadaten der hinterlegten Strategie
};

const berichteSlice = createSlice({
  name: 'berichte',
  initialState,
  reducers: {
    waehleBericht(state, action) {
      state.gewaehlteId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBerichte.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchBerichte.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.liste = action.payload;
        // Standardmaessig den neuesten Bericht auswaehlen.
        if (!state.gewaehlteId && action.payload.length > 0) {
          state.gewaehlteId = action.payload[0].id;
        }
      })
      .addCase(fetchBerichte.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchBerichtHtml.pending, (state) => {
        state.htmlStatus = 'loading';
      })
      .addCase(fetchBerichtHtml.fulfilled, (state, action) => {
        state.htmlStatus = 'succeeded';
        state.htmlCache[action.payload.id] = action.payload.html;
      })
      .addCase(fetchBerichtHtml.rejected, (state) => {
        state.htmlStatus = 'failed';
      })
      .addCase(generiereBericht.pending, (state) => {
        state.generiereStatus = 'loading';
      })
      .addCase(generiereBericht.fulfilled, (state) => {
        state.generiereStatus = 'succeeded';
      })
      .addCase(generiereBericht.rejected, (state) => {
        state.generiereStatus = 'failed';
      })
      .addCase(fetchStatus.fulfilled, (state, action) => {
        state.jobs = action.payload;
      })
      .addCase(fetchStrategie.fulfilled, (state, action) => {
        state.strategie = action.payload;
      });
  },
});

export const { waehleBericht } = berichteSlice.actions;

export const selectBerichte = (state) => state.berichte.liste;
export const selectBerichteStatus = (state) => state.berichte.status;
export const selectGewaehlteBerichtId = (state) => state.berichte.gewaehlteId;
export const selectBerichtHtml = (id) => (state) => state.berichte.htmlCache[id];
export const selectBerichtHtmlStatus = (state) => state.berichte.htmlStatus;
export const selectGeneriereStatus = (state) => state.berichte.generiereStatus;
export const selectJobs = (state) => state.berichte.jobs;
export const selectJob = (job) => (state) =>
  state.berichte.jobs.find((j) => j.job === job) || null;
export const selectStrategie = (state) => state.berichte.strategie;

export default berichteSlice.reducer;
